"use client";

import { VARGA_CHARTS, type VargaDef } from "./divisional-charts-data";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

export default function DivisionalChartsStudio() {
  const [selectedId, setSelectedId] = useState<string>("d9");

  const varga = useMemo<VargaDef>(() => {
    return VARGA_CHARTS.find((v) => v.id === selectedId) || VARGA_CHARTS[1];
  }, [selectedId]);

  const handleConsult = () => {
    const prompt = `Please analyze my ${varga.name} (${varga.sanskrit}) chart. What are the key planetary positions for ${varga.domain}?`;
    try {
      safeSessionSet("seo_entry_intent", "chart_report");
      safeSessionSet("seo_entry_path", "/free-kundali");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader />
      <VargaTabs selectedId={selectedId} onSelect={setSelectedId} />
      <VargaDetailCard varga={varga} onConsult={handleConsult} />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Vedic Micro-Analysis
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        Shodashvarga Divisional Charts (D1, D9 Navamsha, D10 Dashamsha)
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Check divisional micro-charts for marriage and spouse traits (D9 Navamsha), career
        trajectory (D10 Dashamsha), and progeny (D7 Saptamsha).
      </p>
    </div>
  );
}

function VargaTabs({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 mb-6">
      {VARGA_CHARTS.map((v) => {
        const isSelected = v.id === selectedId;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onSelect(v.id)}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-body-small font-semibold transition-all ${
              isSelected
                ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
            }`}
          >
            <span>{v.symbol}</span>
            <span>{v.name.split(" ")[0]}</span>
            <span className="text-caption text-text-muted">({v.sanskrit.split(" ")[0]})</span>
          </button>
        );
      })}
    </div>
  );
}

function VargaDetailCard({ varga, onConsult }: { varga: VargaDef; onConsult: () => void }) {
  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/free-kundali"),
    "/free-kundali",
    "en"
  );

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {varga.symbol} {varga.name} ({varga.sanskrit})
            </h3>
            <span className="px-3 py-0.5 rounded-full text-caption font-bold bg-intent-brand/10 text-intent-brand">
              {varga.division}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Signifies: <strong className="text-text-primary">{varga.domain}</strong>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Read My {varga.name.split(" ")[0]} Chart ✦
        </a>
      </div>

      {/* Interactive SVG Chart Visualizer */}
      <InteractiveChakraVisualizer varga={varga} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-2">
            Key Life Domains Examined
          </p>
          <ul className="space-y-1.5 text-caption text-text-primary">
            {varga.governs.map((gov, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <span className="text-intent-brand">•</span>
                <span>{gov}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-1">
            Primary Karaka Grahas
          </p>
          <p className="text-body-small font-semibold text-text-primary mb-3">{varga.keyGrahas}</p>
          <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-1">
            Classical Jyotishi Rule
          </p>
          <p className="text-caption text-text-secondary leading-relaxed">{varga.specialCheck}</p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Generate your full birth chart to view exact divisional planetary degrees.
        </span>
        <Link
          href={varga.link}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Open Dedicated {varga.name.split(" ")[0]} Guide →
        </Link>
      </div>
    </div>
  );
}

function InteractiveChakraVisualizer({ varga }: { varga: VargaDef }) {
  const [style, setStyle] = useState<"north" | "south">("north");
  const [selectedHouse, setSelectedHouse] = useState<number>(1);

  const HOUSE_MEANINGS: Record<number, string> = {
    1: "Lagna / Self: Core constitution, identity and life trajectory in this division.",
    2: "Dhana / Wealth: Assets, speech, resources and family lineage in this division.",
    3: "Sahaja / Courage: Siblings, communication, initiatives and short travels.",
    4: "Sukha / Comfort: Real estate, vehicles, maternal bond and inner peace.",
    5: "Putra / Intellect: Children, past-life merits (Purva Punya), intelligence and creativity.",
    6: "Ari / Obstacles: Health discipline, debts, competitive challenges and daily service.",
    7: "Kalatra / Partnership: Marriage spouse, business alliances and public relations.",
    8: "Randhra / Transformation: Longevity, hidden wisdom, sudden shifts and occult insight.",
    9: "Bhagya / Dharma: Fortune, higher spirituality, guru guidance and father's blessings.",
    10: "Karma / Profession: Career leadership, public standing, legacy and authority.",
    11: "Labha / Gains: Financial gains, elder siblings, social network and fulfilled desires.",
    12: "Vyaya / Moksha: Spiritual liberation, foreign journeys, subconscious dreams and expenses.",
  };

  return (
    <div className="my-6 rounded-xl border border-border-default bg-surface-raised p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <span className="text-caption font-bold uppercase tracking-wider text-intent-brand">
            Interactive Chart Visualizer
          </span>
          <h4 className="text-body font-bold text-text-primary">
            {varga.name.split(" ")[0]} ({varga.sanskrit}) Chakra
          </h4>
        </div>
        <div className="flex rounded-lg border border-border-default bg-surface-raised p-1 text-caption font-semibold">
          <button
            type="button"
            onClick={() => setStyle("north")}
            className={`px-3 py-1 rounded-md transition-all ${
              style === "north"
                ? "bg-surface-brand-subtle text-intent-brand font-bold shadow-xs"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            North Indian (Diamond)
          </button>
          <button
            type="button"
            onClick={() => setStyle("south")}
            className={`px-3 py-1 rounded-md transition-all ${
              style === "south"
                ? "bg-surface-brand-subtle text-intent-brand font-bold shadow-xs"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            South Indian (Square)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-6 items-center">
        <div className="flex justify-center">
          {style === "north" ? (
            <NorthIndianChakraSvg selectedHouse={selectedHouse} onSelect={setSelectedHouse} />
          ) : (
            <SouthIndianChakraSvg selectedHouse={selectedHouse} onSelect={setSelectedHouse} />
          )}
        </div>

        <div className="rounded-lg border border-border-default bg-surface-raised p-4">
          <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-border-default">
            <span className="text-body-small font-bold text-text-primary">
              House {selectedHouse} (भाव {selectedHouse})
            </span>
            <span className="px-2 py-0.5 rounded text-caption font-bold bg-intent-brand/10 text-intent-brand">
              {varga.name.split(" ")[0]} Significance
            </span>
          </div>
          <p className="text-caption leading-relaxed text-text-secondary">
            {HOUSE_MEANINGS[selectedHouse]}
          </p>
          <div className="mt-3 pt-2 border-t border-border-default">
            <p className="text-caption text-text-muted">
              Click any house in the chart above to inspect its specific Vedic portfolio.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const NORTH_HOUSE_POSITIONS = [
  { h: 1, x: 150, y: 80 },
  { h: 2, x: 80, y: 40 },
  { h: 3, x: 40, y: 80 },
  { h: 4, x: 80, y: 150 },
  { h: 5, x: 40, y: 220 },
  { h: 6, x: 80, y: 260 },
  { h: 7, x: 150, y: 220 },
  { h: 8, x: 220, y: 260 },
  { h: 9, x: 260, y: 220 },
  { h: 10, x: 220, y: 150 },
  { h: 11, x: 260, y: 80 },
  { h: 12, x: 220, y: 40 },
];

function NorthIndianChakraSvg({
  selectedHouse,
  onSelect,
}: {
  selectedHouse: number;
  onSelect: (h: number) => void;
}) {
  return (
    <svg
      viewBox="0 0 300 300"
      className="w-full max-w-[280px] h-auto rounded-lg border border-border-default bg-surface-raised shadow-xs"
    >
      <rect
        x="10"
        y="10"
        width="280"
        height="280"
        fill="none"
        stroke="var(--border-default)"
        strokeWidth="2"
      />
      <line x1="10" y1="10" x2="290" y2="290" stroke="var(--border-default)" strokeWidth="1.5" />
      <line x1="290" y1="10" x2="10" y2="290" stroke="var(--border-default)" strokeWidth="1.5" />
      <polygon
        points="150,10 290,150 150,290 10,150"
        fill="none"
        stroke="var(--intent-brand-strong)"
        strokeWidth="2"
      />

      {NORTH_HOUSE_POSITIONS.map((pos) => {
        const isSelected = selectedHouse === pos.h;
        return (
          <g key={pos.h} onClick={() => onSelect(pos.h)} className="cursor-pointer group">
            <circle
              cx={pos.x}
              cy={pos.y - 4}
              r={isSelected ? "14" : "11"}
              fill={isSelected ? "var(--intent-brand-strong)" : "var(--surface-raised)"}
              stroke={isSelected ? "var(--intent-brand-strong)" : "var(--border-default)"}
              strokeWidth="1.5"
            />
            <text
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              fontSize="11"
              fontWeight="bold"
              fill={isSelected ? "var(--text-on-brand)" : "var(--text-primary)"}
            >
              {pos.h}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const SOUTH_HOUSE_POSITIONS = [
  { h: 12, x: 45, y: 45 },
  { h: 1, x: 115, y: 45 },
  { h: 2, x: 185, y: 45 },
  { h: 3, x: 255, y: 45 },
  { h: 11, x: 45, y: 115 },
  { h: 4, x: 255, y: 115 },
  { h: 10, x: 45, y: 185 },
  { h: 5, x: 255, y: 185 },
  { h: 9, x: 45, y: 255 },
  { h: 8, x: 115, y: 255 },
  { h: 7, x: 185, y: 255 },
  { h: 6, x: 255, y: 255 },
];

function SouthIndianChakraSvg({
  selectedHouse,
  onSelect,
}: {
  selectedHouse: number;
  onSelect: (h: number) => void;
}) {
  return (
    <svg
      viewBox="0 0 300 300"
      className="w-full max-w-[280px] h-auto rounded-lg border border-border-default bg-surface-raised shadow-xs"
    >
      <rect
        x="10"
        y="10"
        width="280"
        height="280"
        fill="none"
        stroke="var(--border-default)"
        strokeWidth="2"
      />
      <rect
        x="80"
        y="80"
        width="140"
        height="140"
        fill="var(--surface-warm)"
        stroke="var(--border-default)"
        strokeWidth="1.5"
      />
      <line x1="80" y1="10" x2="80" y2="290" stroke="var(--border-default)" strokeWidth="1" />
      <line x1="150" y1="10" x2="150" y2="80" stroke="var(--border-default)" strokeWidth="1" />
      <line x1="150" y1="220" x2="150" y2="290" stroke="var(--border-default)" strokeWidth="1" />
      <line x1="220" y1="10" x2="220" y2="290" stroke="var(--border-default)" strokeWidth="1" />
      <line x1="10" y1="80" x2="290" y2="80" stroke="var(--border-default)" strokeWidth="1" />
      <line x1="10" y1="150" x2="80" y2="150" stroke="var(--border-default)" strokeWidth="1" />
      <line x1="220" y1="150" x2="290" y2="150" stroke="var(--border-default)" strokeWidth="1" />
      <line x1="10" y1="220" x2="290" y2="220" stroke="var(--border-default)" strokeWidth="1" />

      {SOUTH_HOUSE_POSITIONS.map((pos) => {
        const isSelected = selectedHouse === pos.h;
        return (
          <g key={pos.h} onClick={() => onSelect(pos.h)} className="cursor-pointer">
            <circle
              cx={pos.x}
              cy={pos.y - 2}
              r={isSelected ? "13" : "10"}
              fill={isSelected ? "var(--intent-brand-strong)" : "var(--surface-raised)"}
              stroke={isSelected ? "var(--intent-brand-strong)" : "var(--border-default)"}
              strokeWidth="1.5"
            />
            <text
              x={pos.x}
              y={pos.y + 2}
              textAnchor="middle"
              fontSize="10"
              fontWeight="bold"
              fill={isSelected ? "var(--text-on-brand)" : "var(--text-primary)"}
            >
              {pos.h}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
